import { ComponentProps, useMemo } from 'react';
import { RoutableProps, RoutePathsWithParams } from '@sd/shared';
import queryString from 'query-string';
import { Route, useLocation, useRouteMatch } from 'react-router-dom';
import { NotFoundScene } from '../NotFoundScene/NotFoundScene';

/**
 * Creates a component that, when used in a route, will automatically have its route and query params passed
 * into the component as props.
 *
 * @see RouteWithParams
 *
 * This allows for creation of components that do not need to know where they are routed from.
 */
function routable<TProps extends Record<string, string | undefined>>(Component: React.ComponentType<TProps>) {
  return () => {
    const location = useLocation();
    const match = useRouteMatch<TProps>();
    const params = match.params;
    const search = queryString.parse(location.search, { arrayFormat: 'comma' });
    const allParams = useMemo(() => ({ ...params, ...search }), [match.params, location.search]);
    return <Component {...allParams} />;
  };
}

type Props<TProps extends RoutableProps> = Omit<ComponentProps<typeof Route>, 'component' | 'path'> & {
  path: RoutePathsWithParams<TProps>;
  component: React.ComponentType<TProps> | React.FC<TProps>;
  permitted?: boolean;
};

/**
 * Creates a route to a component and passes in all route and query params directly into the component's props
 */
export function RouteWithParams<TProps extends RoutableProps>(props: Props<TProps>) {
  const { component, permitted = true, ...routeProps } = props;
  const memoComponent = useMemo(() => routable(component), [component]);
  return <Route {...routeProps} component={permitted ? memoComponent : NotFoundScene} />;
}

type ProtectedRouteProps = ComponentProps<typeof Route> & {
  permitted: boolean;
};

export const ProtectedRoute = ({ permitted, component, ...rest }: ProtectedRouteProps) => (
  <Route component={permitted ? component : NotFoundScene} {...rest} />
);
